import { forwardRef, memo, useState } from 'react';
import type { MutableRefObject } from 'react';

import { useIntersectionObserver } from './utils';

import type { ImageEntry } from '../types';

import STYLES from './ScrollContainer.module.scss';

type Props = {
  images: ImageEntry[];
  onVisible: (visibleIndex: number) => void;
  imagesRef: MutableRefObject<Array<HTMLElement | null>>;
};

const ScrollContainer = memo(({ images, imagesRef, onVisible }: Props) => {
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const observeImageChange = useIntersectionObserver(onVisible, {
    root,
    threshold: 0.5,
  });
  const observeCycleScroll = useIntersectionObserver(
    (index) => {
      const imageElement = imagesRef.current && imagesRef.current[index];
      if (imageElement) {
        imageElement.scrollIntoView({
          block: 'nearest',
          inline: 'start',
        });
      }
    },
    { root, threshold: 1 },
  );

  if (images.length === 1) {
    return (
      <div
        className={STYLES.container}
        data-testid="image-gallery-scroll-container"
      >
        <Image image={images[0]} index={0} />
      </div>
    );
  }

  return (
    <div
      className={STYLES.container}
      ref={setRoot}
      data-testid="image-gallery-scroll-container"
      role="list"
    >
      <Image
        image={images[images.length - 1]}
        index={images.length - 1}
        ref={(el) => {
          observeCycleScroll(el);
          observeImageChange(el);
        }}
      />
      {images.map((image, index) => (
        <Image
          key={image.url}
          image={image}
          index={index}
          ref={(el) => {
            // eslint-disable-next-line no-param-reassign
            imagesRef.current[index] = el;
            observeImageChange(el);
          }}
        />
      ))}
      <Image
        image={images[0]}
        index={0}
        ref={(el) => {
          observeCycleScroll(el);
          observeImageChange(el);
        }}
      />
    </div>
  );
});

type ImageProps = {
  image: ImageEntry;
  index: number;
};
const Image = forwardRef<HTMLDivElement, ImageProps>(
  ({ image, index }, ref) => (
    <div
      className={STYLES.imageContainer}
      key={image.url}
      data-index={index}
      ref={ref}
      role="listitem"
    >
      <img src={image.url} alt={image.altText || ''} className={STYLES.image} />
    </div>
  ),
);

export default ScrollContainer;
